import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, AfterViewInit, Inject } from '@angular/core';
import { Observable, Subscription, BehaviorSubject, of, concat, combineLatest } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { isEmpty, find, Dictionary, includes, chain, isNil } from 'lodash';
import { LabTestResult } from '../lab-test-results';
import { CvdChartData, CvdBandDataValue, AfterChartRenderedEventArgs } from '@covance/ng-cvd3';
import { LabTestsChartData, LabTestsChartYAxisTick, LabTestsChartXAxisTick, LabTestsChartDataResult,
         LabTestsChartDataGenerator, LabTestsChartDataValue } from './lab-tests-chart-data-generator';
import { select } from 'd3';
import { dateTimeRenderer } from '../covance-ag-grid';
import { CovanceChainFilter, FilterFieldValue, FilteredEntityField } from '../covance-filter';
import { FormControl } from '@angular/forms';
import { ChartLegendItem } from '../legend';
import { LabTestsChartSvgExtensions } from './lab-tests-chart-svg-extensions';
import { TooltipFormatter } from '../shared/tooltip-formatter';
import {TooltipItem} from '../shared/tooltip-item';
import { tooltipClassName } from '../core/tooltip-remover';
import { SUBJECT_LAB_TESTS_DATA_SOURCE, SubjectLabTestsDataSource } from './subject-lab-tests-data-source';

@Component({
    selector: 'lab-test-chart',
    template: require('./lab-tests-chart.component.html'),
    styles: [require('./lab-tests-chart.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LabTestsChartComponent implements OnDestroy, AfterViewInit {
    public chartData: LabTestsChartData[] = null;
    public chartBandData: CvdChartData<CvdBandDataValue>[];
    public xAxisTickValues: number[];
    public yAxisTickValues: number[];
    public yDomain : number[] = [];
    public readonly chartMargin = {
        left: 300,
        right: 100,
        top: 30,
        bottom: 50
    };

    public readonly chainFilterFields: Observable<FilteredEntityField<LabTestResult>[]>;
    public readonly initChainFilterFields: Observable<FilterFieldValue[]>;

    public filterFieldVals: BehaviorSubject<FilterFieldValue[]> = new BehaviorSubject([]);

    public simpleSearch = new FormControl('');
    public simpleSearchFlow: Observable<string>;
    public subjectLegendData: ChartLegendItem[] = [];

    private subscription: Subscription;
    private visits: LabTestsChartXAxisTick[] = [];
    private tests: LabTestsChartYAxisTick[] = [];

    constructor(
        @Inject(SUBJECT_LAB_TESTS_DATA_SOURCE) private selectedSubjectLabResultsStorage: SubjectLabTestsDataSource,
        private changeDetectorRef: ChangeDetectorRef,
        private labTestsChartDataGenerator: LabTestsChartDataGenerator,
        private covanceChainFilter: CovanceChainFilter,
        private tooltipFormatter: TooltipFormatter
    ) {
        const testGroup: FilteredEntityField<LabTestResult> = { title: 'Test Group', fieldName: 'testGroup' };
        const test: FilteredEntityField<LabTestResult> = { title: 'Test', fieldName: 'test' };

        this.chainFilterFields = of<FilteredEntityField<LabTestResult>[]>([testGroup, test]);

        this.initChainFilterFields = of<FilterFieldValue[]>([
            {
                def: {
                    title: testGroup.title,
                    queryParam: '0',
                    isPinned: true
                },
                filteredFieldName: testGroup.fieldName
            },
            {
                def: {
                    title: test.title,
                    queryParam: '1',
                    isPinned: true
                },
                filteredFieldName: test.fieldName
            }
        ]);

        this.simpleSearchFlow = concat(of(''), this.simpleSearch.valueChanges);
    }

    public readonly bandColor = () => '#cccc';

    public xAxisTickFormat = (visitId: number) => {
        const visit = find(this.visits, x => x.visitId === visitId);
        return visit ? visit.visitTitle : '';
    }

    public yAxisTickFormat = (y: number) => {
        const test = find(this.tests, x => x.value === y);
        return test ? test.yTickText : '';
    }

    public afterChartRendered(args: AfterChartRenderedEventArgs) {
        const svgChart = select<SVGElement, LabTestsChartData[]>('#' + args.svgId);
        const availableHeight = args.height - (this.chartMargin.top + this.chartMargin.bottom);
        const availableWidth = args.width - (this.chartMargin.left + this.chartMargin.right);
        const yAxisWidth = this.chartMargin.left;

        const chartExtensions = new LabTestsChartSvgExtensions (
            yAxisWidth,
            svgChart,
            args.yScale,
            availableHeight,
            availableWidth,
            this.xAxisTickValues,
            this.tests,
            this.chartBandData);

        chartExtensions.render();
    }

    public chartTooltip = (key: string, visitTitle: string, y: string, args: LabTestChartEvent): string => {
        const labTestResult = args.data.labTestResult;

        const entry: Dictionary<string> = {
            'Visit Description': labTestResult.visitName,
            'Collection Date/Time': dateTimeRenderer({ value: labTestResult.visitCollectionDate }) || '[missing date]',
            'Accession': labTestResult.accessionNumber,
            'Test Group': labTestResult.testGroup,
            'Test': labTestResult.test,
            'Result Comments': labTestResult.comments,
            'Range': labTestResult.currentResult.range,
            'Unit': args.data.testUnit || ''
        };

        const valueEntry = new TooltipItem();
        valueEntry.labelText = 'Value';
        valueEntry.contentText = !isNil(args.data.testNumbericResult) ? args.data.testNumbericResult.toString() : '';
        valueEntry.contentClass = `${tooltipClassName}-${args.data.colorClassName}`;

        if (!isEmpty(labTestResult.flags)) {
            entry['Flags'] = labTestResult.flags.map(f => f.fullName).join(' ');
        }

        this.tooltipFormatter.clear();
        this.tooltipFormatter.addItems(entry);
        this.tooltipFormatter.addTooltipItems([valueEntry]);

        return this.tooltipFormatter.getTooltipContent();
    }

    public ngAfterViewInit() {
        this.subscription = this.createChartData()
            .subscribe((data: LabTestsChartDataResult) => {
                this.chartData = data.chartData;
                this.chartBandData = data.chartBandData;
                this.visits = data.xAxisTicks;
                this.xAxisTickValues = data.xAxisTickValues;
                this.tests = data.yAxisTicks;
                this.yAxisTickValues = data.yAxisTickValues;
                this.yDomain = data.yDomain;

                this.hideAxises();
                this.buildLegendData();

                //TODO: Find the actual reason why change detection doesn't work as expected
                setTimeout(() => this.changeDetectorRef.markForCheck());
            });
    }

    public ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    private createChartData(): Observable<LabTestsChartDataResult> {
        const labTestResultsWithoutQuestions = this.selectedSubjectLabResultsStorage.labTestResults
            .pipe(
                map((results: LabTestResult[]) => {
                    // show spinner
                    if (results === null) {
                        return null;
                    }

                    return results.filter((result: LabTestResult) => !result.isQuestion);
                })
            );

        const filteredByChain = this.covanceChainFilter.getFilteredData(
                labTestResultsWithoutQuestions,
                this.chainFilterFields,
                this.initChainFilterFields
            )
            .pipe(
                tap(data => {
                    this.filterFieldVals.next(data.deserializedFieldValues);
                }),
                map(data => {
                    return data.entities;
                })
            );

        const filteredBySimpleSearch = this.filterBySimpleSearch(filteredByChain);
        return filteredBySimpleSearch.pipe(map((filtered: LabTestResult[]) => this.labTestsChartDataGenerator.generateData(filtered)));
    }

    private buildLegendData() {
        const legend = chain(this.chartData)
            .map(r => r.values)
            .flatten()
            .uniqBy((f: LabTestsChartDataValue) => f.colorClassName)
            .orderBy((f: LabTestsChartDataValue) => f.colorClassName)
            .map((r: LabTestsChartDataValue) =>
                ({
                    shape: r.shape,
                    class: r.colorClassName,
                    label: r.colorClassName,
                    key: null
                }))
            .value();

        this.subjectLegendData = legend;
    }

    private filterBySimpleSearch(data: Observable<LabTestResult[]>): Observable<LabTestResult[]> {
        return combineLatest(
            this.simpleSearchFlow,
            data
        )
        .pipe(
            map(([searchTerm, labTestResults]) => {
                if (isEmpty(searchTerm) || !labTestResults) {
                    return labTestResults;
                }

                const term = searchTerm.trim().toLowerCase();

                const filtered = labTestResults.filter((labTestResult: LabTestResult) => {
                    if (!isEmpty(labTestResult.test) && includes(labTestResult.test.toLowerCase(), term)) {
                        return true;
                    }

                    if (!isEmpty(labTestResult.testGroup) && includes(labTestResult.testGroup.toLowerCase(), term)) {
                        return true;
                    }

                    return false;
                });

                return filtered;
            })
        );
    }

    //TODO: Workaround for cvd3: When data is empty, hide the axises.
    // Besides, afterChartRendered is not being invoked when empty data, so it can't be placed in afterChartRendered.
    private hideAxises() {
        const hasData = !isEmpty(this.chartData) && this.chartData.some((e) => !isEmpty(e.values));
        select('#lab-tests-chart g.cv-y.cv-axis').style('display', !hasData ? 'none' : 'block');
        select('#lab-tests-chart g.cv-x.cv-axis').style('display', !hasData ? 'none' : 'block');
    }
}

export interface LabTestChartEvent {
    d3event: MouseEvent;
    elemIndex: number;
    pos: number[];
    data: LabTestsChartDataValue;
}
